import React from "react";
import {
  Container,
  Grid,
  Skeleton,
  Box,
  Stack,
  Paper,
  Divider,
} from "@mui/material";
import CommonBackground from "../../CommonBackground";

/**
 * Shimmer loading component for StoryDetailsPage
 * Matches the structure of StoryHero, StoryDescription, StoryMetadata, ChaptersSection, and Sidebar
 */
const StoryDetailsPageShimmer = () => {
  return (
    <CommonBackground>
      {/* Hero Section Shimmer */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          mb: { xs: 4, md: 4, lg: 2 },
          pt: { xs: 2, md: 4 },
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4, lg: 6 } }}>
          {/* Mobile Layout Shimmer */}
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
            }}
          >
            {/* Cover Image - Centered */}
            <Box
              sx={{
                width: "100%",
                maxWidth: "280px",
                mb: 3,
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  backgroundColor: "transparent",
                  backdropFilter: "blur(12px)",
                  border: (theme) =>
                    `1px solid ${
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.08)"
                        : "rgba(0,0,0,0.08)"
                    }`,
                  boxShadow: (theme) =>
                    theme.palette.mode === "dark"
                      ? "0 4px 16px rgba(0,0,0,0.2)"
                      : "0 4px 16px rgba(0,0,0,0.1)",
                }}
              >
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  sx={{ aspectRatio: "3/4" }}
                />
              </Paper>
            </Box>

            {/* Title and Author - Centered */}
            <Box sx={{ width: "100%", textAlign: "center", mb: 2.5 }}>
              <Skeleton variant="text" width="80%" height={45} sx={{ mx: "auto", mb: 1.5 }} />
              <Skeleton variant="text" width="70%" height={24} sx={{ mx: "auto", mb: 1.5 }} />
              <Skeleton variant="text" width="50%" height={20} sx={{ mx: "auto", mb: 1 }} />
            </Box>

            {/* Button - Centered */}
            <Box sx={{ width: "100%", maxWidth: "320px", mb: 3 }}>
              <Skeleton
                variant="rounded"
                width="100%"
                height={48}
                sx={{ borderRadius: 999 }}
              />
            </Box>

            {/* Meta Badges - Centered */}
            <Box sx={{ width: "100%", mb: 2.5, display: "flex", justifyContent: "center" }}>
              <Stack direction="row" spacing={1.25} flexWrap="wrap" justifyContent="center" sx={{ gap: 1.25 }}>
                <Skeleton variant="rounded" width={100} height={36.5} sx={{ borderRadius: 999 }} />
                <Skeleton variant="rounded" width={120} height={36.5} sx={{ borderRadius: 999 }} />
              </Stack>
            </Box>

            {/* Stats Row - Centered */}
            <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
              <Stack direction="row" spacing={1.25} flexWrap="wrap" justifyContent="center" sx={{ gap: 1.25 }}>
                <Skeleton variant="rounded" width={80} height={36.5} sx={{ borderRadius: 999 }} />
                <Skeleton variant="rounded" width={100} height={36.5} sx={{ borderRadius: 999 }} />
                <Skeleton variant="rounded" width={120} height={36.5} sx={{ borderRadius: 999 }} />
                <Skeleton variant="rounded" width={100} height={36.5} sx={{ borderRadius: 999 }} />
              </Stack>
            </Box>
          </Box>

          {/* Desktop/Tablet Layout Shimmer */}
          <Grid
            container
            spacing={{ md: 4 }}
            sx={{ display: { xs: "none", md: "flex" } }}
          >
            {/* Left Column: Cover Image */}
            <Grid item xs={12} md={5.5} lg={3.5}>
              <Box
                sx={{
                  position: { lg: "sticky" },
                  top: { lg: 100 },
                  width: "100%",
                  maxWidth: { md: "450px", lg: "none" },
                  mx: { md: "auto", lg: 0 },
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    overflow: "hidden",
                    backgroundColor: "transparent",
                    backdropFilter: "blur(12px)",
                    border: (theme) =>
                      `1px solid ${
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(0,0,0,0.08)"
                      }`,
                    boxShadow: (theme) =>
                      theme.palette.mode === "dark"
                        ? "0 4px 16px rgba(0,0,0,0.2)"
                        : "0 4px 16px rgba(0,0,0,0.1)",
                  }}
                >
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    sx={{ 
                      aspectRatio: "3/4",
                    }}
                  />
                </Paper>
              </Box>
            </Grid>

            {/* Right Column: Story Info */}
            <Grid item xs={12} md={6.5} lg={8.5}>
              <Box>
                {/* Story Title */}
                <Box sx={{ mb: 2.5 }}>
                  <Skeleton variant="text" width="70%" height={56} sx={{ mb: 1.5 }} />
                  <Skeleton variant="text" width="60%" height={24} sx={{ mb: 1.5 }} />
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                    <Skeleton variant="text" width={150} height={20} />
                    <Skeleton variant="text" width={100} height={20} />
                  </Box>
                </Box>

                {/* Meta Badges */}
                <Box sx={{ mb: 2.5 }}>
                  <Stack direction="row" spacing={1.25} flexWrap="wrap" sx={{ gap: 1.25, mb: 2 }}>
                    <Skeleton variant="rounded" width={100} height={36.5} sx={{ borderRadius: 999 }} />
                    <Skeleton variant="rounded" width={120} height={36.5} sx={{ borderRadius: 999 }} />
                  </Stack>
                  <Stack direction="row" spacing={1.25} flexWrap="wrap" sx={{ gap: 1.25 }}>
                    <Skeleton variant="rounded" width={80} height={36.5} sx={{ borderRadius: 999 }} />
                    <Skeleton variant="rounded" width={100} height={36.5} sx={{ borderRadius: 999 }} />
                    <Skeleton variant="rounded" width={120} height={36.5} sx={{ borderRadius: 999 }} />
                    <Skeleton variant="rounded" width={100} height={36.5} sx={{ borderRadius: 999 }} />
                    <Skeleton variant="rounded" width={120} height={36.5} sx={{ borderRadius: 999 }} />
                  </Stack>
                </Box>

                {/* Stats Row */}
                <Box>
                  <Stack direction="row" spacing={1.25} flexWrap="wrap" sx={{ gap: 1.25, mb: 2 }}>
                    <Skeleton variant="rounded" width={100} height={40} sx={{ borderRadius: 999 }} />
                    <Skeleton variant="rounded" width={100} height={40} sx={{ borderRadius: 999 }} />
                  </Stack>
                  <Stack direction="row" spacing={1.25} flexWrap="wrap" sx={{ gap: 1.25 }}>
                    <Skeleton variant="rounded" width={80} height={40} sx={{ borderRadius: 999 }} />
                    <Skeleton variant="rounded" width={80} height={40} sx={{ borderRadius: 999 }} />
                    <Skeleton variant="rounded" width={100} height={40} sx={{ borderRadius: 999 }} />
                    <Skeleton variant="rounded" width={100} height={40} sx={{ borderRadius: 999 }} />
                  </Stack>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Divider Shimmer */}
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4, lg: 6 } }}>
        <Divider
          sx={{
            my: { xs: 3, md: 4 },
            borderColor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.08)",
          }}
        />
      </Container>

      {/* Main Content Layout Shimmer */}
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4, lg: 6 }, pb: { xs: 4, md: 6 } }}>
        <Box
          sx={{
            maxWidth: { xs: "100%", lg: 1400 },
            mx: "auto",
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            gap: { xs: 4, md: 5 },
            alignItems: "flex-start",
          }}
        >
          {/* Main Content - Wide */}
          <Box sx={{ flex: { xs: "1 1 auto", lg: "1 1 65%" }, minWidth: 0, width: "100%" }}>
            {/* Story Description Shimmer */}
            <Paper
              elevation={0}
              sx={{
                mb: { xs: 4, md: 6 },
                p: { xs: 2.5, sm: 3, md: 4, lg: 5 },
                borderRadius: { xs: 2, md: 3 },
                backgroundColor: "transparent",
                backdropFilter: "blur(12px)",
                border: (theme) =>
                  `1px solid ${
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(0,0,0,0.08)"
                  }`,
                boxShadow: (theme) =>
                  theme.palette.mode === "dark"
                    ? "0 2px 8px rgba(0,0,0,0.1)"
                    : "0 2px 8px rgba(0,0,0,0.05)",
              }}
            >
              <Skeleton
                variant="text"
                width="40%"
                height={32}
                sx={{ mb: 3, mx: { xs: "auto", md: 0 } }}
              />
              <Stack spacing={1.5}>
                <Skeleton variant="text" width="100%" height={24} />
                <Skeleton variant="text" width="100%" height={24} />
                <Skeleton variant="text" width="95%" height={24} />
                <Skeleton variant="text" width="100%" height={24} />
                <Skeleton variant="text" width="90%" height={24} />
              </Stack>
            </Paper>

            {/* Story Metadata Shimmer */}
            <Paper
              elevation={0}
              sx={{
                mb: { xs: 4, md: 6 },
                p: { xs: 2.5, sm: 3, md: 4 },
                borderRadius: { xs: 2, md: 3 },
                backgroundColor: "transparent",
                backdropFilter: "blur(12px)",
                border: (theme) =>
                  `1px solid ${
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(0,0,0,0.08)"
                  }`,
                boxShadow: (theme) =>
                  theme.palette.mode === "dark"
                    ? "0 2px 8px rgba(0,0,0,0.1)"
                    : "0 2px 8px rgba(0,0,0,0.05)",
              }}
            >
              <Skeleton
                variant="text"
                width="35%"
                height={32}
                sx={{ mb: 3, mx: { xs: "auto", md: 0 } }}
              />
              <Grid container spacing={{ xs: 2, md: 3 }}>
                {[...Array(6)].map((_, i) => (
                  <Grid item xs={12} sm={6} key={i}>
                    <Box
                      sx={{
                        p: 2.5,
                        borderRadius: 3,
                        backgroundColor: "transparent",
                        backdropFilter: "blur(12px)",
                        border: (theme) =>
                          `1px solid ${
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.08)"
                              : "rgba(0,0,0,0.08)"
                          }`,
                        boxShadow: (theme) =>
                          theme.palette.mode === "dark"
                            ? "0 2px 8px rgba(0,0,0,0.1)"
                            : "0 2px 8px rgba(0,0,0,0.05)",
                      }}
                    >
                      <Skeleton variant="text" width="60%" height={20} sx={{ mb: 1 }} />
                      <Skeleton variant="text" width="80%" height={24} />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>

            {/* Chapters Section Shimmer */}
            <Box sx={{ mb: 6 }}>
              <Box
                sx={{
                  mb: 4,
                  pb: 3,
                  borderBottom: (theme) =>
                    `3px solid ${
                      theme.palette.mode === "dark"
                        ? "rgba(99, 102, 241, 0.3)"
                        : "rgba(59, 130, 246, 0.2)"
                    }`,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "baseline", flexWrap: "wrap", gap: 1, mb: 1 }}>
                  <Skeleton variant="text" width={120} height={36} />
                  <Skeleton variant="text" width={150} height={20} />
                </Box>
              </Box>
              <Stack spacing={2}>
                {[...Array(5)].map((_, i) => (
                  <Paper
                    key={i}
                    elevation={0}
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      backgroundColor: "transparent",
                      backdropFilter: "blur(12px)",
                      border: (theme) =>
                        `1px solid ${
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.08)"
                            : "rgba(0,0,0,0.08)"
                        }`,
                      boxShadow: (theme) =>
                        theme.palette.mode === "dark"
                          ? "0 2px 8px rgba(0,0,0,0.1)"
                          : "0 2px 8px rgba(0,0,0,0.05)",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Skeleton variant="circular" width={40} height={40} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="70%" height={24} sx={{ mb: 1 }} />
                        <Skeleton variant="text" width="50%" height={20} />
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Stack>
            </Box>
          </Box>

          {/* Right Sidebar Shimmer */}
          <Box
            sx={{
              width: { xs: "100%", lg: 320 },
              flexShrink: 0,
              position: { lg: "sticky" },
              top: { lg: 100 },
              alignSelf: "flex-start",
            }}
          >
            {/* Similar Stories Shimmer */}
            <Paper
              elevation={0}
              sx={{
                mb: 3,
                p: { xs: 2.5, md: 3 },
                borderRadius: 3,
                backgroundColor: "transparent",
                backdropFilter: "blur(12px)",
                border: (theme) =>
                  `1px solid ${
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(0,0,0,0.08)"
                  }`,
                boxShadow: (theme) =>
                  theme.palette.mode === "dark"
                    ? "0 2px 8px rgba(0,0,0,0.1)"
                    : "0 2px 8px rgba(0,0,0,0.05)",
              }}
            >
              <Skeleton variant="text" width="50%" height={24} sx={{ mb: 2.5 }} />
              <Stack spacing={1.5}>
                {[...Array(4)].map((_, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: 2,
                    }}
                  >
                    <Skeleton variant="rectangular" width={60} height={50} sx={{ borderRadius: 1.5 }} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="80%" height={20} sx={{ mb: 0.75 }} />
                      <Skeleton variant="text" width="60%" height={16} />
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Paper>

            {/* More from Author Shimmer */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, md: 3 },
                borderRadius: 3,
                backgroundColor: "transparent",
                backdropFilter: "blur(12px)",
                border: (theme) =>
                  `1px solid ${
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(0,0,0,0.08)"
                  }`,
                boxShadow: (theme) =>
                  theme.palette.mode === "dark"
                    ? "0 2px 8px rgba(0,0,0,0.1)"
                    : "0 2px 8px rgba(0,0,0,0.05)",
              }}
            >
              <Skeleton variant="text" width="60%" height={24} sx={{ mb: 2.5 }} />
              <Stack spacing={1.5}>
                <Skeleton variant="rectangular" width={60} height={50} sx={{ borderRadius: 1.5 }} />
                <Skeleton variant="text" width="70%" height={20} />
                <Skeleton variant="text" width="50%" height={16} />
              </Stack>
            </Paper>
          </Box>
        </Box>
      </Container>
    </CommonBackground>
  );
};

export default StoryDetailsPageShimmer;

